/**************
 Cart State
 ***************/

// escape merchant/customer-supplied text before it goes into a template string
function escapeHTML(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// greatest compare-at price for a line item - there are 3 sources (selling plan allocation,
// line item discount, product compare_at_price); returns null when none apply
// How much of an ORDER-level (cart-level) discount was allocated to this line.
//
// Shopify treats the two discount classes very differently in /cart.js, and the difference is
// invisible unless you look for it — verified on this store 2026-08-18 with a live 25% code:
//   * a LINE-level (product) discount reduces final_line_price AND appears in both
//     item.discounts and item.line_level_discount_allocations;
//   * an ORDER-level discount does NOT touch final_line_price at all. It appears in
//     item.discounts, but line_level_discount_allocations stays EMPTY.
// So the order-level share of a line is exactly the gap between the two, and subtracting it is
// what stops the drawer showing a line price the customer is not actually charged. Reading
// item.discounts alone would double-count any bundle discount, which is already inside
// final_line_price.
function cartLevelAllocation(item) {
    const declared = (item.discounts || []).reduce((sum, discount) => sum + (discount.amount || 0), 0);
    const lineLevel = (item.line_level_discount_allocations || []).reduce((sum, allocation) => sum + (allocation.amount || 0), 0);
    return Math.max(0, declared - lineLevel);
}

// What this line actually costs once every discount is counted.
function itemPayablePrice(item) {
    return Math.max(0, item.final_line_price - cartLevelAllocation(item));
}

function itemComparePrice(item, supplement) {
    const comparePrices = [];

    if (item.selling_plan_allocation) {
        comparePrices.push(item.selling_plan_allocation.compare_at_price * item.quantity);
    }

    if (item.original_line_price > item.final_line_price) {
        comparePrices.push(item.original_line_price);
    }

    if (supplement && supplement.compare_at_price) {
        comparePrices.push(supplement.compare_at_price * item.quantity);
    }

    if (comparePrices.length === 0) {
        return null;
    }

    return Math.max(...comparePrices);
}

/**************
 Open Cart
 ***************/

// open cart
async function openCart(skipFetch = false) {
    // open cart if not already open
    if (document.querySelector("#miniCart:not([open])")) {
        document.querySelector("#miniCart").showModal();

        // move initial focus to the dialog
        document.querySelector("#miniCart").focus({preventScroll: true});
    }
    document.querySelector("body").classList.add("noScroll");

    // un-hide the auto-hiding header so the shared backdrop (a fixed child of #header) isn't dragged off-screen by the header's translateY transform - scroll is locked while the cart is open, so it stays put
    document.querySelector("#header")?.classList.remove("scrolledDown");

    // get updated variables
    const [freshCart, freshData] = await Promise.all([getCart(), getCartData()]);

    // update globals
    cart = freshCart;
    cartData = freshData;

    // Split-theme test attribution: re-stamp if this cart was built on the other theme.
    // The drawer is the ONLY cart on this store (/cart redirects to the homepage) and there are
    // no dynamic/express checkout buttons anywhere, so opening it is the last guaranteed moment
    // before checkout — which makes this the right place to catch a cart that crossed cohorts.
    //
    // Deliberately not awaited: the attribute has no bearing on what the drawer renders, so
    // blocking the open on a network round-trip would add latency for zero visible benefit. The
    // .catch keeps a failed stamp from surfacing as an unhandled rejection in production.
    reconcileThemeTestAttribute(cart).catch(() => {});

    // let sitewide listeners (e.g. the PDP free-shipping text) react to the new cart state
    document.dispatchEvent(new CustomEvent("cart:updated"));

    // hide empty cart section
    document.querySelector("#miniCartEmpty").classList.remove("active");

    // update cart count in header nav
    // Optional: the cart-count badge lives in theme-header.liquid, and a layout can legitimately
    // ship the drawer without the site header (the ad landing pages do). Unguarded, this threw
    // after showModal() but before renderItems()/renderFooter() — so the drawer opened empty with
    // no checkout button, and nothing in the UI hinted why.
    document.querySelector("#headerIconsCart")?.setAttribute("data-count", cart.item_count);

    // announce cart state to screen readers via the persistent live region
    const miniCartStatus = document.querySelector("#miniCartStatus");
    if (miniCartStatus) {
        miniCartStatus.textContent = cart.item_count === 0
            ? "Your cart is empty"
            : `${cart.item_count} ${cart.item_count === 1 ? "item" : "items"} in cart, total ${price(qualifyingTotal())}`;
    }

    // free delivery/gift tracker
    renderTracker();

    // check for empty cart and exit
    if (cart.item_count === 0) {
        document.querySelector("#miniCartEmpty").classList.add("active");
        document.querySelector("#miniCartBody").classList.remove("active");
        document.querySelector("#miniCartFooter").classList.remove("active");
        renderEmptyRecommendations();
        return;
    }

    // show main cart sections
    document.querySelector("#miniCartBody").classList.add("active");
    document.querySelector("#miniCartFooter").classList.add("active");

    // line items, upsell row, footer totals
    renderItems();
    renderUpsells();
    renderFooter();

    // stop loading animation
    document.querySelector("#miniCartContent").classList.remove("loading");

    // add/remove the free gift to match the milestone thresholds against the current cart
    await reconcileFreeGift();
}

/**************
 Tracker
 ***************/

// free delivery/gift tracker in the cart header
function renderTracker() {
    const allDots = document.querySelectorAll(".miniCartHeaderTrackerBarDot");
    if (allDots.length === 0) {
        return;
    }

    /*
     A discount milestone advertises an offer the DISCOUNT SYSTEM owns, not the cart. Show it
     only when this session actually holds that offer, matched on the spend minimum.

     The $69 tier belongs to direct and organic visitors. A shopper who arrived on a campaign
     code is on a different offer with no minimum, and telling them to spend $69 to unlock a
     discount they will never be granted is worse than showing no tier at all.

     Fails closed — hidden unless something positively backs it. No discount system on the page
     (it does not ship to the live theme), the kill switch off, a blocked landing layout, no rule
     for this session, or a rule with no minimum: all hide it. Matching on the minimum rather
     than a flag means the row cannot outlive a change to the offer's threshold either.

     Read from `data-dp-minimum` rather than OhSnapDiscount.state(), because this can run before
     the deferred module has loaded and the snippet's PROVISIONAL API does not carry the field —
     it read back undefined, hiding the tier permanently. The dataset has no such gap: the
     snippet stamps it at first paint and publish() is the single chokepoint that maintains it,
     deleting it whenever no rule applies.
    */
    const offerMinimum = parseInt(document.documentElement.dataset.dpMinimum, 10) || 0;
    /*
     The depth the offer ADVERTISES, for {percent}. Read from `data-dp-advertised` — the same
     chokepoint as the minimum above, and the same number the PDP chip announces — so the tracker
     cannot promise a depth the prices on the page are not honouring. Spelling the percentage out
     in the theme setting instead is what let the cart say 20% while the chip said 25%.
    */
    const offerPercent = parseInt(document.documentElement.dataset.dpAdvertised, 10) || 0;
    /*
     The milestone's threshold is typed in the SHOP's currency; the offer's minimum is stamped in
     the SHOPPER's, converted by Shopify at the day's rate (snippets/discount-rules.liquid). Compared
     as they were, a Canadian cart's CA$97.62 never equalled the typed $69 and the tier was hidden
     from every Canadian shopper. So the typed figure is converted at Shopify.currency.rate first,
     with under a cent of slack for Shopify's rounding, and a matched tier then measures against
     the offer's own minimum: the figure checkout enforces, in the cart's currency.

     Only discount tiers convert. Free-shipping and gift tiers keep their typed figures, because
     freeShippingThreshold() and reconcileFreeGift() read those too and must agree with the bar.
    */
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    const backed = el => el.dataset.discountThreshold !== "true"
        || (offerMinimum > 0 && Math.abs(parseInt(el.dataset.threshold, 10) * rate - offerMinimum) < 1);
    const thresholdOf = el => (el.dataset.discountThreshold === "true" ? offerMinimum : parseInt(el.dataset.threshold, 10));

    document.querySelectorAll(".miniCartHeaderTrackerBarDot, .miniCartHeaderTrackerListItem")
        .forEach(el => { el.hidden = !backed(el); });

    // An unbacked dot leaves the geometry entirely. Left in, it would still own maxThreshold and
    // rescale the whole bar around a tier the shopper cannot reach.
    const trackerDots = Array.from(allDots).filter(backed);
    if (trackerDots.length === 0) {
        document.querySelector("#miniCartHeaderTrackerBarFill").style.width = "0%";
        return;
    }

    /*
     Two bases, because the milestones measure different things and always have.

     A free-shipping or free-gift tier is earned on what the customer PAYS — qualifyingTotal().
     A discount tier is evaluated by Shopify on the subtotal BEFORE discounts, so measuring it the
     same way would let the discount it unlocks push the cart back under its own threshold.

     Each row and each dot therefore reads its own basis. They are computed once here rather than
     per element: both walk cart.items, and the tracker re-renders on every cart change.
    */
    const payable = qualifyingTotal();
    const preDiscount = preDiscountSubtotal();
    const basisFor = el => (el.dataset.discountThreshold === "true" ? preDiscount : payable);

    Array.from(document.querySelectorAll(".miniCartHeaderTrackerListItem")).filter(backed).forEach(item => {
        const subtotal = basisFor(item);
        const threshold = thresholdOf(item);
        const textEl = item.querySelector(".miniCartHeaderTrackerListItemText");
        // toggle `.reached` on each dot if the order meets its threshold
        if (subtotal >= threshold) {
            textEl.textContent = item.dataset.qualifiedText
                .replace("{threshold}", price(threshold))
                .replace("{percent}", offerPercent);
            item.classList.add("reached");
        } else {
            /*
             {amount} is the gap to this tier on its OWN basis — pre-discount for a discount tier,
             what the cart pays for a free-shipping or gift tier. {threshold} is the tier itself.

             DELIBERATE, and the alternative was built and reverted: a free-shipping tier is
             earned on what is paid, so a shopper on the 20% offer has to add more GOODS than the
             threshold to reach it ($98.74 of goods for a $79 tier), and quoting that instead is
             the arithmetic a shopper can actually act on. It also makes the number jump — at $68
             of goods the gap reads $11.00, and one more dollar trips the discount and moves it to
             $23.80.

             We quote the payable gap anyway, because this theme has to behave exactly like the
             live one while the discount experience is evaluated. Introducing a different free
             shipping number would be a second change riding along with the discount, and the
             point of the test is to measure one thing. Free shipping triggers at $79 of subtotal
             after the discount, for every audience, and the bar says so.
            */
            textEl.textContent = item.dataset.unqualifiedText
                .replace("{amount}", price(Math.max(0, threshold - subtotal)))
                .replace("{threshold}", price(threshold))
                .replace("{percent}", offerPercent);
            item.classList.remove("reached");
        }
    });

    // fill width - dot positions are threshold/maxThreshold (last dot = 100%);
    // snap to each reached dot, then interpolate linearly toward the next one
    const maxThreshold = Math.max(...Array.from(trackerDots).map(thresholdOf));
    let fillPercent = 0;
    let interpolated = false;
    for (let i = 0; i < trackerDots.length; i++) {
        // the dot being evaluated owns the comparison, so a discount dot is judged pre-discount
        // and a shipping dot on what is payable — the same split as the rows above
        const subtotal = basisFor(trackerDots[i]);
        const threshold = thresholdOf(trackerDots[i]);
        const dotPosition = (threshold / maxThreshold) * 100;
        if (subtotal >= threshold) {
            trackerDots[i].classList.add("reached");
            fillPercent = dotPosition;
        } else {
            trackerDots[i].classList.remove("reached");
            if (!interpolated) {
                interpolated = true;
                const prevThreshold = i === 0 ? 0 : thresholdOf(trackerDots[i - 1]);
                const prevPosition = i === 0 ? 0 : (prevThreshold / maxThreshold) * 100;
                const span = threshold - prevThreshold;
                if (span > 0) {
                    const progress = Math.max(0, (subtotal - prevThreshold) / span);
                    fillPercent = prevPosition + (dotPosition - prevPosition) * progress;
                }
            }
        }
    }
    document.querySelector("#miniCartHeaderTrackerBarFill").style.width = `${fillPercent}%`;
}

// add/remove the free-gift line so the cart matches the gift milestone.
// the subtotal excludes the gift itself, so adding it can't re-trigger its own removal.
async function reconcileFreeGift() {
    const milestone = document.querySelector(".miniCartHeaderTrackerListItem[data-gift-variant-id]");
    if (!milestone) {
        return false;
    }

    const threshold = parseInt(milestone.dataset.threshold);
    const variantId = parseInt(milestone.dataset.giftVariantId);

    // what the customer pays, gift excluded so it never counts toward its own threshold
    const subtotal = qualifyingTotal();

    const giftLine = cart.items.find(item => item.properties?._free_gift);
    const qualifies = subtotal >= threshold;

    // already in the right state - nothing to do
    if (qualifies === !!giftLine) {
        return false;
    }

    // move above threshold -> add the gift; drop below -> remove it
    if (qualifies) {
        await addProduct({items: [{id: variantId, quantity: 1, properties: {_free_gift: "true"}}]});
        cart = await getCart();
    } else {
        cart = await updateCart({updates: {[giftLine.key]: 0}});
    }
    return true;
}

/**************
 Line Items
 ***************/

// render the cart line items and wire up their quantity/remove/upgrade controls
/**************
 Missing-design guard

 A personalized item is a Digital on Demand "mirror" variant (SKU prefix DOD-) that the PDP
 customizer adds with its print file attached as _print_file. A DOD- line WITHOUT one never went
 through the customizer - it reached the cart some other way (a link that rebuilds the cart from
 variant ids alone keeps no line properties) - and if it is ordered, DoD receives a job with
 nothing to print. Orders #1579240 and #1581208 were exactly that.

 The drawer is the only cart on this store, so such a line is flagged here in red with a way back
 to the customizer. It deliberately does NOT hold checkout: a bug in this check must never cost a
 sale, so the warning informs and the order-side Flow rule is what stops a design-less job
 reaching DoD (it also covers links that go straight to checkout and never load the theme).
 ***************/
function isMissingDesign(item) {
    return /^DOD-/i.test(item.sku || "") && !(item.properties && item.properties._print_file);
}

// footer heads-up above Checkout while any line needs a design. Informational only: Checkout is
// never disabled, and a fault here can only drop the note
function syncMissingDesignNote() {
    try {
        const button = document.querySelector("#miniCartFooterCheckout");
        if (!button) return;
        const needed = !!(cart && cart.items && cart.items.some(isMissingDesign));
        const note = document.querySelector("#miniCartFooterMissingDesign");
        if (needed && !note) {
            button.insertAdjacentHTML("beforebegin", `<p id="miniCartFooterMissingDesign" role="status">Some items need a design</p>`);
        } else if (!needed && note) {
            note.remove();
        }
    } catch (e) {
        /* the note is optional */
    }
}

// mirror products are the base product's handle with a "personalized-" prefix (SOP naming)
function personalizeUrlFor(item) {
    const handle = item.handle || "";
    const base = handle.replace(/^personalized-/, "");
    return base !== handle ? `${Shopify.routes.root}products/${base}` : Shopify.routes.root;
}

function renderItems() {
    // reset body container
    document.querySelector("#miniCartBodyItems").innerHTML = "";

    // grab the remove-icon template once per render
    const removeIconTemplate = document.querySelector("#miniCartRemoveIconTemplate");

    // append cart
    cart.items.forEach(function (item) {
        // save supplement data
        const supplementData = cartData[item.key];

        // determine if item has properties
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;

        // free gift lines are auto-managed, so they drop the quantity stepper and remove button
        const isGift = !!(item.properties && item.properties._free_gift);

        // variant HTML - render each option on its own line as "Name: Value"
        function variantHTML() {
            if (item.variant_title === null || !item.options_with_values) {
                return "";
            }
            let optionsHTML = "";
            item.options_with_values.forEach(opt => {
                optionsHTML += `<span class="miniCartBodyItemsItemInfoVariantOption">${opt.name}: ${opt.value}</span>`;
            });
            return `<div class="miniCartBodyItemsItemInfoVariant">${optionsHTML}</div>`;
        }

        // Warranty reassurance chip under the variant options — winning state of the 2026-08
        // PDP + cart warranty A/B test, now shown to everyone. Clearing the "Warranty callout"
        // setting in the theme editor turns it off. Skipped on free gift lines, which
        // aren't warrantied products of their own, and on personalized lines: made-to-order
        // items are final sale, covered only by the Personalization Terms (defects within 30
        // days of delivery), not the one-year warranty. Every line the customizer adds carries
        // _design_type (assets/pdp-personalize.js), which is what identifies one.
        function warrantyHTML() {
            const text = cartSettings.warranty_callout_text;
            const isPersonalized = !!(item.properties && item.properties._design_type) || /^DOD-/i.test(item.sku || "");
            if (!text || isGift || isPersonalized) {
                return "";
            }

            // the tooltip body is a richtext setting, so Shopify has already constrained it to a
            // safe tag subset - it goes in as markup on purpose, unlike the escaped label below
            const info = cartSettings.warranty_callout_info;
            const infoHTML = info
                ? `<button class="miniCartBodyItemsItemInfoWarrantyInfo" type="button" aria-label="More info about the warranty" aria-expanded="false">
                        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
                            <path fill="currentColor" d="M8 .833A7.167 7.167 0 1 0 8 15.167 7.167 7.167 0 0 0 8 .833Zm0 13.334A6.167 6.167 0 1 1 8 1.833a6.167 6.167 0 0 1 0 12.334ZM7.25 7.25h1.5v4.583h-1.5V7.25Zm0-3h1.5v1.5h-1.5v-1.5Z"/>
                        </svg>
                    </button>
                    <div class="miniCartBodyItemsItemInfoWarrantyPopover">${info}</div>`
                : "";

            return `
                <div class="miniCartBodyItemsItemInfoWarranty">
                    <svg class="miniCartBodyItemsItemInfoWarrantyIcon" viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true" focusable="false">
                        <path d="M8 1.5 2.5 3.7v4.1c0 3.2 2.2 5.4 5.5 6.7 3.3-1.3 5.5-3.5 5.5-6.7V3.7L8 1.5Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
                        <path d="m5.9 7.9 1.5 1.5 2.9-2.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>${escapeHTML(text)}</span>
                    ${infoHTML}
                </div>
            `;
        }

        // property HTML
        function propertiesHTML() {
            if (!hasProperties) {
                return "";
            }

            let propertiesInner = "";
            for (const key in item.properties) {
                // skip private properties
                if (key.startsWith("_")) {
                    continue;
                }
                if (typeof item.properties[key] === "string" && item.properties[key].includes("https://")) {
                    propertiesInner += `<a href="${item.properties[key]}" target="_blank">${key}</a><br>`;
                    continue;
                }
                propertiesInner += `${key}: ${item.properties[key]}<br>`;
            }

            if (propertiesInner === "") {
                return "";
            }

            return `<div class="miniCartBodyItemsItemInfoProperties">${propertiesInner}</div>`;
        }

        // subscription HTML
        function subscriptionHTML() {
            if (item.selling_plan_allocation) {
                return `<div class="miniCartBodyItemsItemInfoSellingPlan">${item.selling_plan_allocation.selling_plan.name}</div>`;
            }
            return "";
        }

        /*
         Native Shopify bundle components, listed under their parent line.

         The data comes from templates/cart.supplemental-data.liquid because
         `line_item.item_components` is Liquid-only — /cart.js exposes no components field — so this
         renders nothing at all unless that endpoint supplied some. An ordinary product, or a
         legacy `_bundle` multi-line bundle, has no item_components and is unaffected.

         A native bundle charges the PARENT's price, so a component's own price is not something
         the shopper is being billed for. Showing it would read as a second charge, so only the
         free item is annotated (FREE), and the rest are listed by name and colour.
        */
        function componentsHTML() {
            const components = supplementData && supplementData["item_components"];
            if (!components || !components.length) {
                return "";
            }

            const rows = components.map(function (component) {
                const image = component.image
                    ? `<img class="miniCartBodyItemsItemComponentsItemImage" src="${escapeHTML(component.image)}" alt="" loading="lazy" decoding="async">`
                    : "";

                // a variant title of "Default Title" is Shopify's placeholder for an optionless
                // product and means nothing to a shopper
                const variant = component.variant_title && component.variant_title !== "Default Title"
                    ? `<span class="miniCartBodyItemsItemComponentsItemVariant">${escapeHTML(component.variant_title)}</span>`
                    : "";

                const quantity = component.quantity > 1
                    ? `<span class="miniCartBodyItemsItemComponentsItemQuantity">&times;${component.quantity}</span>`
                    : "";

                const free = component.is_free
                    ? `<span class="miniCartBodyItemsItemComponentsItemFree">Free</span>`
                    : "";

                return `
                    <li class="miniCartBodyItemsItemComponentsItem">
                        ${image}
                        <span class="miniCartBodyItemsItemComponentsItemText">
                            <span class="miniCartBodyItemsItemComponentsItemTitle">${escapeHTML(component.product_title)}</span>
                            ${variant}
                        </span>
                        ${quantity}
                        ${free}
                    </li>
                `;
            }).join("");

            return `<ul class="miniCartBodyItemsItemComponents">${rows}</ul>`;
        }

        // price HTML - current price + (optional) compare-at price
        function priceHTML() {
            // free gift - shown as free, with pre-discount price struck through
            if (item.properties && item.properties._free_gift) {
                return `<span class="miniCartBodyItemsItemPriceCurrent">Free</span><span class="miniCartBodyItemsItemPriceCompare"><span class="visuallyHidden">Regular price</span>${price(item.original_line_price)}</span>`;
            }

            // the price the customer pays for this line, including any order-level code discount
            // that never made it into final_line_price
            const payable = itemPayablePrice(item);
            let HTML = `<span class="miniCartBodyItemsItemPriceCurrent">${price(payable)}</span>`;

            // anchor against the line's own pre-discount value as well as compare-at, so a code
            // discount still strikes through even once compare-at is gone from the catalogue
            const comparePrice = Math.max(itemComparePrice(item, supplementData) || 0, item.final_line_price);
            if (comparePrice > payable) {
                HTML += `<span class="miniCartBodyItemsItemPriceCompare"><span class="visuallyHidden">Regular price</span>${price(comparePrice)}</span>`;
            }

            return HTML;
        }

        // upgrade button HTML
        function upgradeHTML() {
            if (item.selling_plan_allocation) {
                return "";
            }
            if (!supplementData || !supplementData["selling_plan_id"]) {
                return "";
            }

            return `
                <button
                    class="miniCartBodyItemsItemUpgrade button"
                    data-key="${item.key}"
                    data-quantity="${item.quantity}"
                    data-plan="${supplementData["selling_plan_id"]}"
                >
                    ${supplementData["selling_plan_name"]}
                </button>
            `;
        }

        let quantityHTML = `
                    <div class="miniCartBodyItemsItemInfoQuantity">
                        <button class="miniCartBodyItemsItemInfoQuantityMinus" type="button" aria-label="Decrease quantity">−</button>
                        <span class="miniCartBodyItemsItemInfoQuantityCount">${item.quantity}</span>
                        <button class="miniCartBodyItemsItemInfoQuantityPlus" type="button" aria-label="Increase quantity">+</button>
                    </div>`;
        let removeHTML = `<button class="miniCartBodyItemsItemRemove" type="button" aria-label="Remove item"></button>`;
        let giftAttr = "";
        if (isGift) {
            quantityHTML = "";
            removeHTML = "";
            giftAttr = ` data-free-gift="true"`;
        }
        // bundled lines can't change quantity (it would break the bundle's pricing), so swap the
        // stepper for a static "Bundled" chip; the remove button stays so removing still un-bundles
        if (item.properties && item.properties._bundle) {
            quantityHTML = `<div class="miniCartBodyItemsItemInfoBundle">Bundled</div>`;
        }

        // a personalized item that arrived without its design (see the missing-design guard)
        // wrapped so a fault here can only drop the warning, never the drawer it is drawn into
        function missingDesignHTML() {
            try {
                if (!isMissingDesign(item)) {
                    return "";
                }
                return `<div class="miniCartBodyItemsItemInfoMissingDesign">No design added. Remove this item and <a href="${personalizeUrlFor(item)}">personalize it again</a>.</div>`;
            } catch (e) {
                return "";
            }
        }

        // personalized items carry a rendered on-product mockup (Cloudinary URL) that replaces
        // the default variant image in the cart; it's already sized for this thumbnail, so skip
        // Shopify's own CDN "&width=" param, which Cloudinary doesn't understand
        const thumbSrc = (item.properties && item.properties._preview_image) || `${item.image}&width=200`;

        // insert item
        document.querySelector("#miniCartBodyItems").insertAdjacentHTML("beforeend", `
            <div class="miniCartBodyItemsItem" data-key="${item.key}"${giftAttr}>
                <img class="miniCartBodyItemsItemImage" src="${thumbSrc}" alt="${item.product_title}">
                <div class="miniCartBodyItemsItemInfo">
                    <div>
                        <div class="miniCartBodyItemsItemInfoTitle">${item.product_title}</div>
                        ${variantHTML()}
                        ${missingDesignHTML()}
                        ${warrantyHTML()}
                        ${propertiesHTML()}
                        ${subscriptionHTML()}
                        ${componentsHTML()}
                    </div>
                    ${quantityHTML}
                </div>
                <div class="miniCartBodyItemsItemPrice">${priceHTML()}</div>
                ${removeHTML}
                ${upgradeHTML()}
            </div>
        `);

        // inject the remove icon from the template (avoids re-stringifying it on every render)
        const lastItem = document.querySelector("#miniCartBodyItems").lastElementChild;
        const removeButton = lastItem.querySelector(".miniCartBodyItemsItemRemove");
        if (removeIconTemplate && removeButton) {
            removeButton.appendChild(removeIconTemplate.content.cloneNode(true));
        }
    });

    // event listener for each line item
    document.querySelectorAll(".miniCartBodyItemsItem").forEach(function (element) {
        // free gift lines have no quantity/remove controls to wire
        if (element.dataset.freeGift) {
            return;
        }

        const key = element.dataset.key;

        // bundled lines render a chip instead of the stepper, so the count may be absent
        const countEl = element.querySelector(".miniCartBodyItemsItemInfoQuantityCount");
        let quantity = countEl ? parseInt(countEl.textContent) : 0;

        // minus button - decrement, or remove entirely at quantity 1 (absent on bundled lines)
        element.querySelector(".miniCartBodyItemsItemInfoQuantityMinus")?.addEventListener("click", function () {
            if (quantity === 1) {
                element.querySelector(".miniCartBodyItemsItemRemove").click();
            } else {
                quantity--;
                updateCartItem();
            }
        });

        // plus button (absent on bundled lines)
        element.querySelector(".miniCartBodyItemsItemInfoQuantityPlus")?.addEventListener("click", function () {
            quantity++;
            updateCartItem();
        });

        // remove button
        element.querySelector(".miniCartBodyItemsItemRemove").addEventListener("click", function () {
            quantity = 0;
            updateCartItem();
        });

        // push the new quantity to Shopify, then re-render from the fresh cart
        async function updateCartItem() {
            if (countEl) {
                countEl.textContent = quantity;
            }
            document.querySelector("#miniCartContent").classList.add("loading");

            // fully removing a line breaks its bundle, so grab its bundle id
            let removedBundleID = null;
            if (quantity === 0) {
                const removedItem = cart.items.find(item => item.key === key);
                removedBundleID = removedItem?.properties?._bundle_id || null;
            }

            // removing a bundled line also un-bundles its siblings (their discount no longer applies).
            if (removedBundleID) {
                await updateCart({updates: {[key]: quantity}});
                const siblings = cart.items.filter(item => item.properties?._bundle_id === removedBundleID);
                for (const sibling of siblings) {
                    await changeCart({id: sibling.key, properties: {_bundle_id: removedBundleID}});
                }
                await openCart();
                return;
            }

            // plain quantity change: update the line and render from the response
            await updateCart({updates: {[key]: quantity}});
            await openCart();
        }

        // upgrade button - swap the item onto its subscription selling plan
        element.querySelector(".miniCartBodyItemsItemUpgrade")?.addEventListener("click", async function () {
            const upgradeKey = this.dataset.key;
            const upgradeQuantity = parseInt(this.dataset.quantity);
            const sellingPlanID = parseInt(this.dataset.plan);

            document.querySelector("#miniCartContent").classList.add("loading");

            cart = await changeCart({
                id: upgradeKey,
                quantity: upgradeQuantity,
                selling_plan: sellingPlanID
            });

            await openCart();
            this.classList.remove("loading");
        });
    });
}

/**************
 Upsells
 ***************/

// hide upsell cards already in the cart, then cap the visible count so additions
// pull the next pick into view instead of shrinking the row
function renderUpsells() {
    // hide cards for products already in the cart
    document.querySelectorAll(".miniCartBodyUpsellsScrollItem").forEach(element => {
        const productID = parseInt(element.dataset.product);
        const isInCart = cart.items.find(item => item.product_id === productID);
        element.classList.toggle("hidden", !!isInCart);
    });

    // cap the visible count
    const maxVisibleUpsells = parseInt(cartSettings.max_visible_upsells) || 3;
    let visibleUpsellCount = 0;
    document.querySelectorAll(".miniCartBodyUpsellsScrollItem").forEach(element => {
        if (element.classList.contains("hidden")) {
            return;
        }
        if (visibleUpsellCount >= maxVisibleUpsells) {
            element.classList.add("hidden");
        } else {
            visibleUpsellCount++;
        }
    });

    // hide the whole block when nothing is left to show
    const upsellsBlock = document.querySelector("#miniCartBodyUpsells");
    if (upsellsBlock) {
        upsellsBlock.style.display = visibleUpsellCount === 0 ? "none" : "";
    }
}

/**************
 Empty-cart recommendations
 ***************/

// Populates the empty cart with a collection's products as add-to-cart cards, in the
// collection's own sort order. The cards come from the theme-mini-cart-recommendations
// section via the Section Rendering API rather than being built here from products.json:
// they use the same collection-item card as the cart upsells, whose swatches need option
// swatch colours/images and variant metafields that the Ajax endpoints don't return.
// Requesting the section at /collections/<handle> is what gives it collection context, so
// the sort order survives — which a layout-rendered loop would have lost.
// Fetched once, lazily, the first time an empty cart is opened.
// 'idle' → 'loading' → 'done'; left at 'idle' on failure so the next open can retry.
let emptyRecsState = "idle";

async function renderEmptyRecommendations() {
    const container = document.querySelector("#miniCartEmptyRecommendations");
    // feature off (no collection set → the Liquid block isn't rendered), already building, or already built
    if (!container || emptyRecsState === "loading" || emptyRecsState === "done") {
        return;
    }

    const handle = container.dataset.collection;
    const count = parseInt(container.dataset.count) || 8;
    if (!handle) {
        return;
    }

    emptyRecsState = "loading";
    const list = container.querySelector("#miniCartEmptyRecommendationsList");

    let cards;
    try {
        const response = await fetch(`${Shopify.routes.root}collections/${handle}?section_id=theme-mini-cart-recommendations`);
        if (!response.ok) {
            throw new Error(`section render ${response.status}`);
        }
        // parsed into an inert document, so trimming to the configured count happens before
        // anything is live: the cards we drop never request their images. Setting innerHTML
        // first and removing the extras afterwards would fetch every thumbnail we discard.
        const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
        cards = Array.from(parsed.querySelectorAll(".collectionItem")).slice(0, count);
    } catch (e) {
        // leave the block hidden — the empty cart falls back to just its title
        emptyRecsState = "idle";
        return;
    }

    if (cards.length === 0) {
        emptyRecsState = "idle";
        return;
    }

    // the section's own wrapper is left behind — the list supplies the row layout.
    // Cards carry the sitewide .collectionItemAdd button, handled by theme-global.js
    cards.forEach(card => list.appendChild(document.importNode(card, true)));

    // same reason as predictive search: these cards were rendered by Liquid, which has no
    // visibility of the discount code on this session
    window.OhSnapDiscount?.decorate(list);
    list.setAttribute("aria-busy", "false");
    container.hidden = false;
    document.querySelector("#miniCartEmpty").classList.add("hasRecommendations");
    emptyRecsState = "done";
}

/**************
 Footer Totals
 ***************/

// footer subtotal, savings line, and checkout price
function renderFooter() {
    // subtotal is the cart's full compare-at value; savings is the gap between that and
    // what the customer pays, so compare-at markdowns, line-level (bundle) discounts and
    // cart-level codes are each counted exactly once - never derive savings by re-adding
    // cart.total_discount, it already lives inside final_line_price for line-level discounts
    let preDiscountTotal = 0;
    let giftTotal = 0;
    cart.items.forEach(item => {
        // managed free gifts are shown at no cost, so keep their price out of the displayed totals
        if (item.properties && item.properties._free_gift) {
            giftTotal += item.final_line_price;
            return;
        }
        const comparePrice = itemComparePrice(item, cartData[item.key]);
        preDiscountTotal += Math.max(item.final_line_price, comparePrice || 0);
    });

    // what the customer actually pays (the gift's price is excluded to match the
    // discount that zeroes it at checkout) — shared with the tracker so they cannot drift
    const payableTotal = qualifyingTotal();
    const totalSavings = preDiscountTotal - payableTotal;

    // footer totals - subtotal is pre-discount so savings show as a separate line below
    document.querySelector("#miniCartFooterSummarySubtotalAmount").textContent = price(preDiscountTotal);
    document.querySelector("#miniCartFooterCheckoutPrice").textContent = price(payableTotal);
    syncMissingDesignNote();

    // savings pill + amount line, only when there is something saved
    const discountsRow = document.querySelector("#miniCartFooterSummaryDiscounts");
    if (totalSavings > 0) {
        // same rounding as the PDP savings badge: nearest whole percent, then UP to the next multiple of 5
        const rawPercent = Math.round((totalSavings / preDiscountTotal) * 100);
        const discountPercent = Math.ceil(rawPercent / 5) * 5;
        document.querySelector("#miniCartFooterSummaryDiscountsLeftPill").textContent = `SAVING ${discountPercent}%`;
        document.querySelector("#miniCartFooterSummaryDiscountsAmount").textContent = `-${price(totalSavings)}`;
        discountsRow.classList.add("active");
    } else {
        discountsRow.classList.remove("active");
    }

    // shipping line - shown only once the cart clears the free-shipping milestone; below it the
    // row stays hidden, since the client did not want "calculated at checkout" copy here. Reads
    // qualifyingTotal(), the same figure the header tracker and the PDP shipping line use, so the
    // drawer cannot promise free shipping in the tracker and withhold it three rows below. No
    // milestones configured, or a market without free shipping -> null threshold -> never shown.
    const shippingRow = document.querySelector("#miniCartFooterSummaryShipping");
    if (shippingRow) {
        const threshold = freeShippingThreshold();
        shippingRow.classList.toggle("active", threshold !== null && qualifyingTotal() >= threshold);
    }
}

/**************
 Warranty Tooltip
 ***************/

// Info popover on the warranty callout, mirroring the PDP info badges
// (productV1ContentInfoBadgesItemInfo in main-product-v1.js). Delegated from the static
// #miniCartBodyItems container because renderItems() replaces its innerHTML on every cart
// change, which would drop any per-button listener. Inert when the merchant clears the
// warranty settings, where warrantyHTML() renders nothing for these selectors to match.

// breathing room kept between the popover and the drawer's inner edges
const MINI_CART_POPOVER_GUTTER = 8;

function closeWarrantyPopovers() {
    document.querySelectorAll(".miniCartBodyItemsItemInfoWarrantyPopover.active").forEach(function (popover) {
        popover.classList.remove("active");
        popover.classList.remove("flipUp");
        popover.style.left = "";
        popover.parentElement.querySelector(".miniCartBodyItemsItemInfoWarrantyInfo").setAttribute("aria-expanded", "false");
    });
}

document.querySelector("#miniCartBodyItems").addEventListener("click", function (e) {
    const infoBtn = e.target.closest(".miniCartBodyItemsItemInfoWarrantyInfo");
    if (!infoBtn) {
        return;
    }

    const popover = infoBtn.parentElement.querySelector(".miniCartBodyItemsItemInfoWarrantyPopover");
    const willOpen = !popover.classList.contains("active");

    // only one open at a time, same as the PDP badges
    closeWarrantyPopovers();

    if (!willOpen) {
        return;
    }

    popover.classList.add("active");
    infoBtn.setAttribute("aria-expanded", "true");

    // #miniCartBody is overflow-y: auto (which makes overflow-x auto too), so the popover is
    // clipped on both axes. Clamp it to the drawer rather than to the viewport: the drawer is
    // what actually bounds it, and a vw-based max-width would guess wrong at every breakpoint.
    const bodyRect = document.querySelector("#miniCartBody").getBoundingClientRect();
    const rect = popover.getBoundingClientRect();

    if (rect.bottom > bodyRect.bottom) {
        popover.classList.add("flipUp");
    }

    const overflowRight = rect.right - (bodyRect.right - MINI_CART_POPOVER_GUTTER);
    if (overflowRight > 0) {
        // left is 0 by default, so the shift is just the negative overflow
        popover.style.left = `${Math.max(-rect.left + bodyRect.left + MINI_CART_POPOVER_GUTTER, -overflowRight)}px`;
    }
});

// close on outside click and on Escape (Escape must not also close the cart dialog)
document.addEventListener("mousedown", function (e) {
    if (e.target.closest(".miniCartBodyItemsItemInfoWarranty")) {
        return;
    }
    closeWarrantyPopovers();
});

document.querySelector("#miniCart").addEventListener("keydown", function (e) {
    if (e.key !== "Escape") {
        return;
    }
    if (document.querySelector(".miniCartBodyItemsItemInfoWarrantyPopover.active")) {
        e.preventDefault();
        e.stopPropagation();
        closeWarrantyPopovers();
    }
});

/**************
 Open / Close Events
 ***************/

// close event
document.querySelector("#miniCart").addEventListener("close", function () {
    document.querySelector("body").classList.remove("noScroll");
});

/*
 The tracker's discount tier depends on which offer this session holds, and that is not settled
 until assets/theme-discount.js has read a real cart — after the drawer may already have
 rendered. Without this, a tier the session lost stays on screen until the next cart change.

 Safe before the cart loads: qualifyingTotal() returns 0 on an undefined cart, and renderTracker
 returns early when the theme has no milestone blocks at all.
*/
document.addEventListener("discount:changed", renderTracker);

// close cart function
function closeCart() {
    document.querySelector("#miniCart").close();
}

// open cart event listener
document.querySelectorAll(".openCart").forEach(function (element) {
    element.addEventListener("click", function (e) {
        e.preventDefault();
        openCart();
    });
});

// close cart event listener
document.querySelectorAll(".closeCart").forEach(function (element) {
    element.addEventListener("click", closeCart);
});

// close cart on backdrop click - using coords avoids misfires on the dialog's own padding
document.querySelector("#miniCart").addEventListener("click", function (e) {
    if (e.detail === 0) {
        return;
    }
    const rect = this.getBoundingClientRect();
    const insideDialog = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!insideDialog) {
        closeCart();
    }
});

// checkout button
document.querySelector("#miniCartFooterCheckout").addEventListener("click", async function () {
    this.classList.add("loading");
    // Shoplift checkout test (snippets/checkout-test-gate.liquid): stamp the cart's arm before
    // leaving. Always resolves, within its own cap, so it can never hold checkout.
    if (window.checkoutTestStamp) {
        await window.checkoutTestStamp();
    }
    window.location.href = `${Shopify.routes.root}checkout`;
});

/**************
 Upsells Carousel
 ***************/

// horizontal free-scroll swiper for the upsell row
const upsellsScroll = document.querySelector("#miniCartBodyUpsellsScroll");
if (upsellsScroll) {
    new Swiper(upsellsScroll, {
        slidesPerView: "auto",
        spaceBetween: 10,
        freeMode: true,
        grabCursor: true,
        mousewheel: {
            forceToAxis: true
        },
        observer: true,
        observeParents: true
    });
}

/**************
 Init
 ***************/

// warm the supplemental-data cache so the first open renders complete
if (cart.item_count > 0) {
    getCartData().then(data => cartData = data);
}

// check is coming from the cart
if (document.referrer.includes("/cart")) {
    openCart();
}
